import React from "react";
import moment from "moment-timezone";

import { Button, DatePicker, Drawer, Form, Input, InputNumber, message } from "antd";

import { _postSupplierPriceListLine, _putSupplierPriceListLine } from "../../services";
import { TIMEZONE, errorMessage, toNumberOrNull } from "../term-contract/helpers";
import { defaultValidTo } from "./helpers";
import LinkedProduct from "./linked-product";

const TEXT_FIELDS = [
    { name: "supplierCode", label: "Supplier code" },
    { name: "title", label: "Title", required: true },
    { name: "brand", label: "Brand" },
    { name: "model", label: "Model" },
    { name: "spec", label: "Spec" },
    { name: "uom", label: "UOM", help: "Blank = EA" },
    { name: "remarks", label: "Remarks" }
];
const MAX_TIERS = 5;

const toMoment = ({
    date,
}) => date ? moment.tz(date, TIMEZONE) : null;

const fromLine = ({
    line,
}) => {
    const today = moment.tz(TIMEZONE);
    if (!line) {
        return { costPrice: undefined, listPrice: undefined, moq: undefined, tiers: [], productId: "", sku: "", validFrom: today, validTo: moment.tz(defaultValidTo({ date: today }), TIMEZONE) };
    }
    return {
        ...TEXT_FIELDS.reduce((state, f) => ({ ...state, [f.name]: line[f.name] || "" }), {}),
        costPrice: line.costPrice === null ? undefined : line.costPrice,
        listPrice: line.listPrice === null ? undefined : line.listPrice,
        moq: line.moq === null ? undefined : line.moq,
        tiers: (line.tiers || []).map(t => ({ minQty: t.minQty, costPrice: t.costPrice, listPrice: t.listPrice })),
        productId: line.productId ? `${line.productId}` : "",
        validFrom: toMoment({ date: line.validFrom }),
        validTo: toMoment({ date: line.validTo })
    };
};

/** Add (no `line`) or edit a line: every field, its tiers, its validity, and its product link (a product ID typed here is a manual link). */
class LineDrawer extends React.Component {

    state = { ...fromLine({ line: this.props.line }), saving: false };

    body = () => {
        const { line } = this.props;
        const { costPrice, listPrice, moq, tiers, productId, sku, validFrom, validTo } = this.state;
        const body = {
            ...TEXT_FIELDS.reduce((out, f) => ({ ...out, [f.name]: (this.state[f.name] || "").trim() || null }), {}),
            costPrice: toNumberOrNull({ value: costPrice }),
            listPrice: toNumberOrNull({ value: listPrice }),
            moq: toNumberOrNull({ value: moq }),
            tiers: tiers.map(t => ({ minQty: toNumberOrNull({ value: t.minQty }), costPrice: toNumberOrNull({ value: t.costPrice }), listPrice: toNumberOrNull({ value: t.listPrice }) }))
        };
        /* an empty date is left out: the server defaults validFrom to today and says validTo is required */
        if (validFrom) body.validFrom = validFrom.format("YYYY-MM-DD");
        if (validTo) body.validTo = validTo.format("YYYY-MM-DD");
        /* only a changed product ID is sent: sending it makes the link manual */
        const currentProductId = line && line.productId ? `${line.productId}` : "";
        if (productId.trim() !== currentProductId) body.productId = productId.trim() || null;
        if (!line && sku.trim()) body.sku = sku.trim();
        return body;
    };

    save = async () => {
        const { priceList, line, onSaved } = this.props;
        this.setState({ saving: true });
        try {
            const { data } = line
                ? await _putSupplierPriceListLine({ id: priceList._id, lineId: line._id, data: this.body() })
                : await _postSupplierPriceListLine({ id: priceList._id, data: this.body() });
            (data.warnings || []).forEach(warning => message.warning(warning));
            message.success(line ? "Line saved" : "Line added");
            onSaved();
        } catch (e) {
            message.error(errorMessage({ error: e }));
            this.setState({ saving: false });
        }
    };

    setTier = ({
        index,
        field,
        value,
    }) => this.setState(state => ({ tiers: state.tiers.map((t, i) => i === index ? { ...t, [field]: value } : t) }));

    render() {
        const { line, onClose } = this.props;
        const { costPrice, listPrice, moq, tiers, productId, sku, validFrom, validTo, saving } = this.state;
        return (
            <Drawer visible width={560} title={line ? `Edit ${line.title}` : "Add line"} onClose={onClose} bodyStyle={{ paddingBottom: 80 }}>
                <Form layout="vertical">
                    {TEXT_FIELDS.map(f => (
                        <Form.Item key={f.name} label={f.label} required={f.required} help={f.help} style={{ marginBottom: 8 }}>
                            <Input value={this.state[f.name]} onChange={e => this.setState({ [f.name]: e.target.value })} />
                        </Form.Item>
                    ))}
                    <div style={{ display: "flex", gap: 12 }}>
                        <Form.Item label="Cost price" style={{ flex: 1 }}>
                            <InputNumber min={0} value={costPrice} style={{ width: "100%" }} onChange={value => this.setState({ costPrice: value })} />
                        </Form.Item>
                        <Form.Item label="List price" style={{ flex: 1 }}>
                            <InputNumber min={0} value={listPrice} style={{ width: "100%" }} onChange={value => this.setState({ listPrice: value })} />
                        </Form.Item>
                        <Form.Item label="MOQ" style={{ flex: 1 }}>
                            <InputNumber min={1} precision={0} value={moq} style={{ width: "100%" }} onChange={value => this.setState({ moq: value })} />
                        </Form.Item>
                    </div>
                    <div style={{ color: "rgba(0,0,0,0.45)", fontSize: 12, marginTop: -8, marginBottom: 12 }}>
                        Before tax. At least one of cost and list price.
                    </div>
                    <Form.Item label="Quantity tiers" help="The base price applies below the first tier.">
                        {tiers.map((t, index) => (
                            <div key={index} style={{ display: "flex", gap: 8, marginBottom: 4 }}>
                                <InputNumber placeholder="min qty" min={2} precision={0} value={t.minQty} onChange={value => this.setTier({ index, field: "minQty", value })} />
                                <InputNumber placeholder="cost" min={0} value={t.costPrice} onChange={value => this.setTier({ index, field: "costPrice", value })} />
                                <InputNumber placeholder="list" min={0} value={t.listPrice} onChange={value => this.setTier({ index, field: "listPrice", value })} />
                                <Button icon="delete" aria-label="Remove tier" onClick={() => this.setState(state => ({ tiers: state.tiers.filter((_, i) => i !== index) }))} />
                            </div>
                        ))}
                        {tiers.length < MAX_TIERS && (
                            <Button size="small" icon="plus" onClick={() => this.setState(state => ({ tiers: [...state.tiers, {}] }))}>Add tier</Button>
                        )}
                    </Form.Item>
                    <div style={{ display: "flex", gap: 12 }}>
                        <Form.Item label="Valid from" style={{ flex: 1 }}>
                            <DatePicker value={validFrom} format="YYYY-MM-DD" onChange={value => this.setState({ validFrom: value })} />
                        </Form.Item>
                        <Form.Item label="Valid to" required style={{ flex: 1 }}>
                            <DatePicker value={validTo} format="YYYY-MM-DD" onChange={value => this.setState({ validTo: value })} />
                        </Form.Item>
                    </div>
                    <Form.Item
                        label="Linked eezee product ID"
                        help={line && line.productId
                            ? <span><LinkedProduct line={line} />. Change or clear it to link by hand.</span>
                            : "Optional. Linking by hand is never overwritten by an upload."}
                    >
                        <Input value={productId} onChange={e => this.setState({ productId: e.target.value })} />
                    </Form.Item>
                    {!line && (
                        <Form.Item label="…or eezee SKU" help="Links to the product with this SKU.">
                            <Input value={sku} onChange={e => this.setState({ sku: e.target.value })} />
                        </Form.Item>
                    )}
                </Form>
                <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, padding: "10px 16px", borderTop: "1px solid #e8e8e8", background: "#fff", textAlign: "right" }}>
                    <Button onClick={onClose}>Cancel</Button>{" "}
                    <Button type="primary" loading={saving} onClick={this.save}>{line ? "Save" : "Add line"}</Button>
                </div>
            </Drawer>
        );
    }
}

export default LineDrawer;



// WEBPACK FOOTER //
// ./src/components/supplier-price-list/line-drawer.js